import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "locales";
import "./style.scss";

const MAX_DURATION = 45; // seconds

interface Props {
  onSend: (blob: Blob, duration: number, textOnly: boolean) => void;
  onCancel: () => void;
}

function VoiceRecorder({ onSend, onCancel }: Props) {
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [textOnly, setTextOnly] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const autoStoppedRef = useRef(false);
  const startedRef = useRef(false);
  // Always-current onCancel so the start effect can run exactly once without
  // capturing a stale callback (parent passes a fresh inline arrow each render).
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const doSend = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") return;
    if (timerRef.current) clearInterval(timerRef.current);

    recorder.onstop = () => {
      recorder.stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
      const duration = (Date.now() - startTimeRef.current) / 1000;
      onSend(blob, duration, textOnly);
    };
    recorder.stop();
    setRecording(false);
  }, [onSend, textOnly]);

  const startRecording = useCallback(async () => {
    try {
      if (!navigator.mediaDevices) {
        alert(t("Microphone requires HTTPS or localhost. Current page is not secure."));
        onCancelRef.current();
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.start(1000);
      recorderRef.current = recorder;
      startTimeRef.current = Date.now();
      autoStoppedRef.current = false;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsed(secs);
      }, 500);
    } catch (err) {
      console.error("Microphone access denied:", err);
      onCancelRef.current();
    }
  }, [t]);

  // Start ONCE on mount. Keying this on `startRecording` (whose identity
  // followed the parent's inline `onCancel`) made every parent re-render tear
  // down the live MediaRecorder and restart it — the reported "any button
  // resets the recording" bug. The empty-dep + startedRef guard fixes that;
  // the cleanup still stops the recorder on real unmount.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startRecording();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stream.getTracks().forEach((t) => t.stop());
        recorderRef.current.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-stop at max duration
  useEffect(() => {
    if (elapsed >= MAX_DURATION && recording && !autoStoppedRef.current) {
      autoStoppedRef.current = true;
      doSend();
    }
  }, [elapsed, recording, doSend]);

  const handleCancel = useCallback(() => {
    // Let the user keep an in-progress recording instead of losing it to a
    // stray tap on the cancel control.
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      if (!window.confirm(t("Discard this recording?"))) return;
    }
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stream.getTracks().forEach((t) => t.stop());
      recorder.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
    onCancel();
  }, [onCancel, t]);

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const progress = Math.min(elapsed / MAX_DURATION, 1);

  return (
    <div className="voice-recorder">
      <button className="voice-recorder__cancel" onClick={handleCancel} type="button" title={t("Cancel")}>
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor" />
        </svg>
      </button>
      <div className="voice-recorder__indicator">
        <span className="voice-recorder__dot" />
        <div className="voice-recorder__progress-wrap">
          <div className="voice-recorder__progress-bar" style={{ width: `${progress * 100}%` }} />
        </div>
        <span className="voice-recorder__time">{formatTime(elapsed)} / {formatTime(MAX_DURATION)}</span>
      </div>
      <button
        className={`voice-recorder__text-only${textOnly ? " __active" : ""}`}
        onClick={() => setTextOnly((v) => !v)}
        type="button"
        title={textOnly ? t("Send as text (on)") : t("Send as text")}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 7V4h16v3" />
          <path d="M9 20h6" />
          <path d="M12 4v16" />
        </svg>
      </button>
      <button className="voice-recorder__send" onClick={doSend} type="button" title={t("Send")}>
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
        </svg>
      </button>
    </div>
  );
}

export default VoiceRecorder;
